// src/utils/financialIndicators.js

/**
 * Altman Z-Score
 * Z = 1.2*(WC/TA) + 1.4*(RE/TA) + 3.3*(EBIT/TA) + 0.6*(MVE/TA) + 1.0*(S/TA)
 * @param {{ totalCurrentAssets, totalCurrentLiabilities, retainedEarnings, ebit, totalAssets, revenue, commonStockSharesOutstanding }} bs
 * @param {number} price  // share price at this date
 */
export function altmanZScore(bs, price) {
  const { totalCurrentAssets, totalCurrentLiabilities, retainedEarnings, ebit, totalAssets, revenue, commonStockSharesOutstanding } = bs;
  const WC   = totalCurrentAssets - totalCurrentLiabilities;
  const RE   = retainedEarnings;
  const EBIT = ebit;
  const S    = revenue;
  const TA   = totalAssets;
  // market value of equity
  const MVE  = price * commonStockSharesOutstanding;
  return (
    1.2 * (WC / TA) +
    1.4 * (RE / TA) +
    3.3 * (EBIT / TA) +
    0.6 * (MVE / TA) +
    1.0 * (S / TA)
  );
}

/**
 * Piotroski F-Score (0–9)
 * @param {{ netIncome }} incCurr     // this period’s income
 * @param {{ netIncome: prevNetIncome }} incPrev  // prior period’s income
 * @param {{ operatingCashFlow }} cfCurr
 * @param {{ totalAssets, totalCurrentAssets, totalCurrentLiabilities, longTermDebt, commonStockSharesOutstanding }} bsCurr
 * @param {{ totalAssets, totalCurrentAssets, totalCurrentLiabilities, longTermDebt, commonStockSharesOutstanding }} bsPrev
 * @param {{ grossProfit, revenue }} incGPRevCurr
 * @param {{ grossProfit: prevGrossProfit, revenue: prevRevenue }} incGPRevPrev
 */
export function piotroskiFScore(incCurr, incPrev, cfCurr,
                                bsCurr, bsPrev,
                                incGPRevCurr, incGPRevPrev) {
  let score = 0;

  // PROFITABILITY
  if (incCurr.netIncome > 0) score++;                                    // NI > 0
  if (cfCurr.operatingCashFlow > 0) score++;                              // CFO > 0
  const roaCurr = incCurr.netIncome / bsCurr.totalAssets;
  const roaPrev = incPrev.netIncome / bsPrev.totalAssets;
  if (roaCurr > roaPrev) score++;                                         // ROA↑
  if (cfCurr.operatingCashFlow > incCurr.netIncome) score++;               // CFO > NI

  // LEVERAGE / LIQUIDITY
  const levCurr = bsCurr.longTermDebt / bsCurr.totalAssets;
  const levPrev = bsPrev.longTermDebt / bsPrev.totalAssets;
  if (levCurr < levPrev) score++;                                         // Leverage ↓
  const crCurr = bsCurr.totalCurrentAssets / bsCurr.totalCurrentLiabilities;
  const crPrev = bsPrev.totalCurrentAssets / bsPrev.totalCurrentLiabilities;
  if (crCurr > crPrev) score++;                                           // Current ratio ↑
  if (bsCurr.commonStockSharesOutstanding <= bsPrev.commonStockSharesOutstanding) score++; // No dilution

  // OPERATING EFFICIENCY
  const gmCurr = incGPRevCurr.grossProfit / incGPRevCurr.revenue;
  const gmPrev = incGPRevPrev.prevGrossProfit / incGPRevPrev.prevRevenue;
  if (gmCurr > gmPrev) score++;                                           // Gross margin ↑
  const atCurr = incGPRevCurr.revenue / bsCurr.totalAssets;
  const atPrev = incGPRevPrev.prevRevenue / bsPrev.totalAssets;
  if (atCurr > atPrev) score++;                                           // Asset turnover ↑

  return score;
}

/**
 * Beneish M-Score (flags earnings manipulation; higher = worse)
 * M = –4.84
 *     +0.92*DSRI
 *     +0.528*GMI
 *     +0.404*AQI
 *     +0.892*SGI
 *     +0.115*DEPI
 *     –0.172*SGAI
 *     +4.679*LVGI
 *     +0.327*TATA
 *     –0.327*EM
 *
 * For full definitions of DSRI, GMI, etc., see:
 *   https://en.wikipedia.org/wiki/Beneish_M-score
 *
 * @param {{ daysSalesOutstanding, grossProfitMargin, assetTurnover,
 *            depreciationAndAmortization, sellingGeneralAndAdministrativeExpenses,
 *            totalAssets, etc. }} mCurr  current-period metrics
 * @param {{ same fields }} mPrev        prior-period metrics
 */
export function beneishMScore(mCurr, mPrev) {
  // DSRI = (A/R_t / Sales_t) / (A/R_t-1 / Sales_t-1)
  const DSRI = (mCurr.daysOfSalesOutstanding / (1/mCurr.assetTurnover)) /
               (mPrev.daysOfSalesOutstanding / (1/mPrev.assetTurnover));
  // GMI = (Sales_t-1 - COGS_t-1)/Sales_t-1  /  (Sales_t - COGS_t)/Sales_t
  const GMI  = ((1 - (mPrev.costOfRevenue / mPrev.revenue))   /
                (1 - (mCurr.costOfRevenue / mCurr.revenue)));
  // AQI, SGI, DEPI, SGAI, LVGI, TATA, EM are similar—omitted here for brevity
  const AQI = 1;   // ... compute per-spec ...
  const SGI = mCurr.revenue / mPrev.revenue;
  const DEPI = (mPrev.depreciationAndAmortization /
                (mPrev.depreciationAndAmortization + mPrev.propertyPlantEquipment)) /
               (mCurr.depreciationAndAmortization /
                (mCurr.depreciationAndAmortization + mCurr.propertyPlantEquipment));
  const SGAI = (mCurr.sellingGeneralAndAdministrativeExpenses / mCurr.revenue) /
               (mPrev.sellingGeneralAndAdministrativeExpenses / mPrev.revenue);
  const LVGI = (mCurr.totalDebt / mCurr.totalAssets) /
               (mPrev.totalDebt / mPrev.totalAssets);
  const TATA = (mCurr.operatingCashFlow - mCurr.netIncome) / mCurr.totalAssets;
  const EM   = mCurr.totalAssets / mCurr.totalEquity;

  return (
    -4.84 +
    0.92 * DSRI +
    0.528 * GMI +
    0.404 * AQI +
    0.892 * SGI +
    0.115 * DEPI -
    0.172 * SGAI +
    4.679 * LVGI +
    0.327 * TATA -
    0.327 * EM
  );
}

/**
 * Montier C-Score (0–6; higher = more manipulation risk)
 * Six binary tests: Accruals, Depreciation, ROA, Leverage, Liquidity, Sales
 */
export function montierCScore(mCurr, mPrev) {
  let score = 0;
  // Accruals
  const accruals = (mCurr.operatingCashFlow - mCurr.netIncome) / mCurr.totalAssets;
  if (accruals < 0) score++;
  // Depreciation growth
  if ((mCurr.depreciationAndAmortization / mCurr.totalAssets) >
      (mPrev.depreciationAndAmortization / mPrev.totalAssets)) score++;
  // ROA decline
  const roaCurr = mCurr.netIncome / mCurr.totalAssets;
  const roaPrev = mPrev.netIncome / mPrev.totalAssets;
  if (roaCurr < roaPrev) score++;
  // Leverage increase
  if ((mCurr.totalDebt / mCurr.totalAssets) >
      (mPrev.totalDebt / mPrev.totalAssets)) score++;
  // Liquidity decline
  if ((mCurr.currentRatio) <
      (mPrev.currentRatio)) score++;
  // Sales growth < CPI growth? (requires CPI data externally)
  // if ((mCurr.revenue / mPrev.revenue) < (CPI_t / CPI_t-1)) score++;

  return score;
}

/**
 * Ohlson O-Score (probability of bankruptcy)
 * See: Ohlson (1980)
 * O = −1.32 − 0.407*log(TA/GNP) + 6.03*TLTA − 1.43*WCTA + 0.0767*CLCA
 *     − 1.72*OENEG − 2.37*NITA − 1.83*FUTL + 0.285*INTWO − 0.521*CHIN
 * Where each term is defined in Ohlson’s paper.
 * Implementing it fully requires GNP data for scaling.
 */
export function ohlsonOScore(mCurr, mPrev, gnp) {
  // You can fill in the full formula here
  return null;
}

/**
 * Cash Conversion Cycle (CCC)
 * = DIO + DSO − DPO
 * @param {{ daysOfInventoryOutstanding, daysOfSalesOutstanding, daysOfPayablesOutstanding }} m
 */
export function cashConversionCycle(m) {
  return (
    m.daysOfInventoryOutstanding +
    m.daysOfSalesOutstanding -
    m.daysOfPayablesOutstanding
  );
}

/** Accounts Receivable Turnover = Revenue / AvgReceivables */
export function receivablesTurnover(m) {
  return m.receivablesTurnover;
}

/** Days Payable Outstanding = Days of Payables Outstanding */
export function daysPayableOutstanding(m) {
  return m.daysOfPayablesOutstanding;
}

/** EV/EBITDA Yield = EBITDA / Enterprise Value */
export function evEbitdaYield(m) {
  // if the API gives you EV/EBITDA directly:
  // 1) raw EBITDA / EV
  if (m.ebitda != null && m.enterpriseValue != null) {
    return m.ebitda / m.enterpriseValue;
  }
  // 2) invert any EV/EBITDA multiple if present
  if (m.evToEbitda != null) {
    return 1 / m.evToEbitda;
  }
  // 3) invert EV/EBIT multiple as last resort
  if (m.enterpriseValueMultiple != null) {
    return 100 / m.enterpriseValueMultiple;
  }
  return null;
}

/**
 * Dividend metrics
 * @param {{ dividendsPaid }} cf
 * @param {{ netIncome }} inc
 * @param {number} price
 */
export function dividendPayoutRatio(cf, inc) {
  return Math.abs(cf.dividendsPaid) / inc.netIncome;
}
export function dividendYield(cf, price) {
  return Math.abs(cf.dividendsPaid) / price;
}

/**
 * DuPont ROE decomposition
 * ROE = NetProfitMargin * AssetTurnover * EquityMultiplier
 * @param {{ netProfitMargin, assetTurnover, companyEquityMultiplier }} m
 */
export function dupontROE(m) {
  return (
    m.netProfitMargin *
    m.assetTurnover *
    m.companyEquityMultiplier
  );
}
